import * as React from 'react';

import { toMoney } from '@rematter/common/money';
import { enumBillStatus } from '@rematter/frontend-lib-api-client';
import {
  Button,
  FooterItem,
  humanReadableNumber,
  Money as MoneyField,
  Row,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  $currentDraftBillInfo,
  $discardAllDrafts,
  $effectiveLineItems,
  $remoteBillDetail,
  $updateBill,
} from '../../_state/billDetail.state';
import { useTranslations } from '../../i18n/useTranslations';
import { BillRecord } from '../../types';

import { toMoneyInput } from '~/modules/trade/utils/money';
import { getLocale } from '~/shared/utils/locale';

//
// ⚛️ ⚛️ ⚛️ BILL DETAIL FOOTER ⚛️ ⚛️ ⚛️
//
export const BillDetailFooter = () => {
  // Translations
  const t = useTranslations();
  const toast = useToast();

  // Atoms
  const queryResult = useAtomValue($remoteBillDetail);
  const bill = (queryResult?.data as { bill: BillRecord | null } | undefined)
    ?.bill;
  const effectiveLineItems = useAtomValue($effectiveLineItems);
  const draftBillInfo = useAtomValue($currentDraftBillInfo);
  const updateBill = useSetAtom($updateBill);
  const discardAllDrafts = useSetAtom($discardAllDrafts);

  // State
  const [isSaving, setIsSaving] = React.useState(false);

  const isDisabled = React.useMemo(
    () =>
      bill?.status === enumBillStatus.Paid ||
      bill?.status === enumBillStatus.Voided,
    [bill?.status],
  );

  const totalPaid = React.useMemo(() => {
    if (!bill?.amount || !bill?.openAmount) return null;
    return toMoney({
      amount: bill.amount.amount - bill.openAmount.amount,
      currencyCode: bill.amount.currencyCode,
    });
  }, [bill?.amount, bill?.openAmount]);

  // Callbacks
  const handleSave = React.useCallback(async () => {
    if (!bill?.id || isSaving) return;

    setIsSaving(true);
    try {
      const lineItemInputs = effectiveLineItems
        .filter((item) => item.description.trim() !== '')
        .map((item) => ({
          id: item.id.startsWith('temp-') ? undefined : item.id,
          description: item.description,
          quantity: item.quantity ?? 0,
          unit: item.unit ?? '',
          unitPrice: toMoneyInput(item.unitPrice),
          totalPrice: toMoneyInput(item.totalPrice),
          itemId: item.itemId ?? undefined,
        }));

      await updateBill({
        id: bill.id,
        documentDate:
          draftBillInfo?.documentDate ?? bill.documentDate ?? undefined,
        documentNumber:
          draftBillInfo?.documentNumber ?? bill.documentNumber ?? undefined,
        eligibleAt: draftBillInfo?.eligibleAt ?? bill.eligibleAt ?? undefined,
        preferredPaymentMethod:
          draftBillInfo?.preferredPaymentMethod ??
          bill.preferredPaymentMethod ??
          undefined,
        notes: draftBillInfo?.notes ?? bill.notes ?? undefined,
        amount: draftBillInfo?.amount
          ? toMoneyInput(draftBillInfo.amount)
          : bill.amount
            ? toMoneyInput(bill.amount)
            : undefined,
        facilityId: draftBillInfo?.facilityId ?? bill.facilityId ?? undefined,
        lineItems: lineItemInputs,
      });

      toast.success(t.messages.bills.updateSuccess);
    } catch (err) {
      toast.error(
        t.bills.detail.messages.updateError(
          err instanceof Error ? err.message : t.messages.common.unknownError,
        ),
      );
    } finally {
      setIsSaving(false);
    }
  }, [bill, isSaving, effectiveLineItems, draftBillInfo, updateBill, toast, t]);

  const handleDiscard = React.useCallback(() => {
    discardAllDrafts();
  }, [discardAllDrafts]);

  if (!bill) return null;

  return (
    <Row xEnd yCenter gap5 className="@BillDetailFooter">
      <Row xEnd yCenter gap8 className="[&>*]:min-h-auto">
        <FooterItem label={t.bills.detail.footer.totalAmount}>
          <MoneyField
            className="text-lg leading-none font-bold"
            {...bill.amount}
            locale={getLocale()}
          />
        </FooterItem>
        <FooterItem label={t.bills.detail.footer.totalPaid}>
          <MoneyField
            className="text-lg leading-none font-bold"
            {...totalPaid}
            locale={getLocale()}
          />
        </FooterItem>
        <FooterItem label={t.bills.detail.footer.numberOfPayments}>
          <span className="text-lg leading-none font-bold">
            {humanReadableNumber(bill.payments?.length ?? 0, {
              locale: getLocale(),
              decimalPlaces: 0,
            })}
          </span>
        </FooterItem>
        <FooterItem
          className="text-lg leading-none font-bold"
          label={t.bills.detail.footer.openAmount}
        >
          <MoneyField {...bill.openAmount} locale={getLocale()} />
        </FooterItem>
      </Row>
      {!isDisabled && (
        <Row yCenter gap3 className="flex-shrink-0">
          <Button
            color="neutral"
            variant="outline"
            onPress={handleDiscard}
            isDisabled={isSaving}
          >
            {t.bills.detail.unsavedChanges.discard}
          </Button>
          <Button
            color="primary"
            variant="solid"
            onPress={handleSave}
            isPending={isSaving}
            isLoading={isSaving}
          >
            {t.bills.detail.unsavedChanges.save}
          </Button>
        </Row>
      )}
    </Row>
  );
};
